import { cloneDeep } from 'lodash';

const mockElement = () => ({
  mount: jest.fn(),
  destroy: jest.fn(),
  on: jest.fn(),
  off: jest.fn(),
  update: jest.fn(),
});

export const mockElements = (additionalElements?: object) => {
  const elements = { ...additionalElements };
  return {
    create: jest.fn((type) => {
      elements[type] = mockElement();
      return elements[type];
    }),
    getElement: jest.fn((type) => {
      return elements[type] || null;
    }),
    update: jest.fn(),
  };
};

export const mockStripeSDK = (additionalFields = {}) => ({
  elements: jest.fn(() => mockElements()),
  createToken: jest.fn(),
  createSource: jest.fn(),
  createPaymentMethod: jest.fn(),
  confirmPayment: jest.fn(),
  confirmCardPayment: jest.fn(),
  confirmCardSetup: jest.fn(),
  paymentRequest: jest.fn().mockImplementation(() => {
    return {
      canMakePayment: jest.fn().mockResolvedValue(null),
    };
  }),
  registerAppInfo: jest.fn(),
  _registerWrapper: jest.fn(),
  ...additionalFields,
});

export const setupMockedStripeWithEvents = ({
  mockedStripe = mockStripeSDK(),
  mockedElements = mockElements(),
  defaultMockedElement = mockElement(),
}) => {
  const mockedElement = cloneDeep(defaultMockedElement);

  mockedStripe.elements.mockReturnValue(mockedElements);
  mockedElements.create.mockReturnValue(mockedElement);

  // stripe is injecting the events through here. this isn't an officially documented approach, but was unpicked from
  // the Stripe SDK's unit tests:
  // https://github.com/stripe/react-stripe-js/blob/1441b7a901b72a2514d1a68deb90b09a6c1ab9da/src/components/createElementComponent.test.tsx#L45-L55
  const simulateElementsEvents = {};
  const simulateOn = jest.fn((event, fn) => {
    simulateElementsEvents[event] = [
      ...(simulateElementsEvents[event] || []),
      fn,
    ];
  });
  const simulateOff = jest.fn((event, fn) => {
    simulateElementsEvents[event] = simulateElementsEvents[event].filter(
      (previouslyAddedFn) => previouslyAddedFn !== fn
    );
  });

  mockedElement.on = simulateOn;
  mockedElement.off = simulateOff;

  return {
    mockedStripe,
    allSimulatedStripeEvents: simulateElementsEvents,
  };
};

export const simulateStripeEvent = (
  allEvents: Record<string, any[]>,
  event: string,
  ...args: any[]
) => {
  allEvents[event].forEach((fn) => fn(...args));
};

export const simulateAsyncStripeEvent = (
  allEvents: Record<string, any[]>,
  event: string,
  ...args: any[]
) => {
  return Promise.resolve(simulateStripeEvent(allEvents, event, ...args));
};
